"use client";

import { differenceInSeconds } from "date-fns";
import { useTranslations } from "next-intl";
import { useEffect, useState } from "react";

import { NoticeBanner } from "./NoticeBanner";

interface ChallengeLockCountdownProps {
  lockAt: Date;
}

interface TimeRemaining {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

function getTimeRemaining(targetDate: Date): TimeRemaining {
  const totalSeconds = Math.max(0, differenceInSeconds(targetDate, new Date()));
  return {
    days: Math.floor(totalSeconds / 86400),
    hours: Math.floor((totalSeconds % 86400) / 3600),
    minutes: Math.floor((totalSeconds % 3600) / 60),
    seconds: totalSeconds % 60,
  };
}

function TimeUnit({
  value,
  label,
  pad,
}: {
  value: number;
  label: string;
  pad?: boolean;
}) {
  const display = pad ? value.toString().padStart(2, "0") : value.toString();
  return (
    <div className="flex items-baseline gap-1">
      <span className="font-cinzel text-lg font-bold tabular-nums text-amber-300">
        {display}
      </span>
      <span className="text-xs text-amber-400/70">{label}</span>
    </div>
  );
}

export function ChallengeLockCountdown({ lockAt }: ChallengeLockCountdownProps) {
  const t = useTranslations();
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(
    null,
  );

  useEffect(() => {
    setTimeRemaining(getTimeRemaining(lockAt));
    const interval = setInterval(() => {
      setTimeRemaining(getTimeRemaining(lockAt));
    }, 1000);
    return () => clearInterval(interval);
  }, [lockAt]);

  if (!timeRemaining) return null;

  const { days, hours, minutes, seconds } = timeRemaining;

  return (
    <NoticeBanner
      className="mb-6"
      accent="warning"
      icon="solar:clock-circle-bold"
      title={t("tournaments.challengesEnding.title")}
      description={t("tournaments.challengesEnding.description")}
    >
      <div className="mt-2.5 flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <TimeUnit value={days} label={t("tournaments.countdown.days")} />
        <TimeUnit value={hours} label={t("tournaments.countdown.hours")} pad />
        <TimeUnit
          value={minutes}
          label={t("tournaments.countdown.minutes")}
          pad
        />
        <TimeUnit
          value={seconds}
          label={t("tournaments.countdown.seconds")}
          pad
        />
      </div>
    </NoticeBanner>
  );
}
